import { TradeItemCreationData } from 'core/types'

export type CSVParseResult = {
    success: TradeItemCreationData[]
    errors: { row: number; message: string }[]
}

const REQUIRED_COLUMNS = [
    'Symbol',       // Instrument alias
    'Avg Price',    // Price entry
    'LTP',          // Price exit
    'Type',         // For isBuy
    'PnL',       // Profit
    'MAE',
    'MFE',
    'Max Size',
    'Entry Time',
    'Exit Time',
] as const

export function readFile(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target?.result as string)
        reader.onerror = () => reject(new Error('Failed to read file'))
        reader.readAsText(file)
    })
}

export function parseCSVContent(content: string): string[][] {
    const lines = content.trim().split('\n')
    return lines.map((line) => {
        const result: string[] = []
        let current = ''
        let inQuotes = false

        for (const char of line) {
            if (char === '"') {
                inQuotes = !inQuotes
            } else if (char === ',' && !inQuotes) {
                result.push(current.trim())
                current = ''
            } else {
                current += char
            }
        }

        result.push(current.trim())
        return result
    })
}

export function csvToTradeItems(csvContent: string, journalId: string): CSVParseResult {
    const rows = parseCSVContent(csvContent)
    if (rows.length < 2) {
        return { success: [], errors: [{ row: 0, message: 'CSV file has no data' }]}
    }

    const headers = rows[0].map((h) => h.toLowerCase().trim())
    const dataRows = rows.slice(1)

    const columnIndex: Record<string, number> = {}
    headers.forEach((header, index) => {
        columnIndex[header] = index
    })

    const missingColumns = REQUIRED_COLUMNS
        .filter(col => columnIndex[col.toLowerCase()] === undefined)
    if (missingColumns.length > 0) {
        return { success: [], errors: [{ row: 0, message: 'Trade data is incomplete' }]}
    }

    const success: TradeItemCreationData[] = []
    const errors: CSVParseResult['errors'] = []

    dataRows.forEach((row, index) => {
        const rowNumber = index + 1;   // +1 because of header row

        try {
            const getValue = (colName: string): string => {
                const idx = columnIndex[colName.toLowerCase()];
                return idx !== undefined ? row[idx] : ''
            }

            const trade: TradeItemCreationData = {
                journalId,
                instrumentAlias: getValue('Symbol'),
                priceEntry: parseFloat(getValue('Avg Price')),
                priceExit: parseFloat(getValue('LTP')),
                isBuy: getValue('Type').toLowerCase() === 'buy',
                profit: parseFloat(getValue('PnL')),
                mae: parseFloat(getValue('MAE')),
                mfe: parseFloat(getValue('MFE')),
                maxSize: parseFloat(getValue('Max Size')),
                timeEntry: getValue('Entry Time').replace(' ', 'T'),
                timeExit: getValue('Exit Time').replace(' ', 'T'),
                // Optional fields
                fills: getValue('Fills') ? parseFloat(getValue('Fills')) : undefined,
                commission: getValue('Commission') ? parseFloat(getValue('Commission')) : undefined,
                postMae: getValue("MAE Post") ? parseFloat(getValue("MAE Post")) : undefined,
                postMfe: getValue("MFE Post") ? parseFloat(getValue("MFE Post")) : undefined,
            }

            const validationError = validateTradeItem(trade)
            if (validationError) {
                errors.push({ row: rowNumber, message: validationError })
            } else {
                success.push(trade)
            }
        } catch (e) {
            errors.push({ row: rowNumber, message: `Failed to parse row: ${e}` })
        }
    })

    return { success, errors }
}

function validateTradeItem(trade: TradeItemCreationData): string | null {
    if (!trade.instrumentAlias) return 'Missing instrument alias'
    if (isNaN(trade.priceEntry)) return 'Invalid price entry'
    if (isNaN(trade.priceExit)) return 'Invalid price exit'
    if (isNaN(trade.profit)) return 'Invalid profit'
    if (isNaN(trade.mae)) return 'Invalid MAE'
    if (isNaN(trade.mfe)) return 'Invalid MFE'
    if (isNaN(trade.maxSize)) return 'Invalid max size'
    if (!trade.timeEntry) return 'Missing time entry'
    if (!trade.timeExit) return 'Missing time exit'
    return null
}